import { Link, useLocation, useNavigate } from 'react-router-dom';

/**
 * The way back from a client's record.
 *
 * **It must be true history back when the client was opened from inside the
 * app.** A link to `/clients` is a different navigation: it pushes a new entry,
 * unmounts whatever list the reader came from, and never runs that list's
 * place-keeping — so a director who scrolled four hundred rows into Visits,
 * opened a client and pressed this button was returned to the client list
 * instead, having lost the list, the rows and the position. That is bug C of 17
 * September, and it defeated the place-keeping entirely rather than merely
 * interacting badly with it: the page that knew where they were was never
 * asked.
 *
 * **Reached directly, there is nothing to go back to.** A bookmark, a pasted
 * link or a new tab has no history entry of ours behind it; `history.back()`
 * would leave the application, and on a fresh tab would do nothing at all. React
 * Router sets `location.key` to `'default'` for the entry a session starts on,
 * which is the honest signal for "this is where they came in".
 *
 * **The label names where it goes**, taken from the state the linking page set.
 * A control that says "Clients" and returns to Reports is worse than one that
 * says nothing, so with no state it says "Back" and still goes back — the label
 * is the part that degrades, never the destination.
 */
export interface FromState {
  /** What to call the page being returned to: "Visits", "Reports", "Clients". */
  from?: string;
}

export function BackControl() {
  const location = useLocation();
  const navigate = useNavigate();
  const state = (location.state ?? null) as FromState | null;

  const className =
    'inline-flex min-h-[44px] items-center text-[0.9375rem] font-semibold ' +
    'text-neutral-600 hover:text-neutral-900';

  // 'default' is the key React Router gives the entry the session started on:
  // nothing of ours precedes it, so there is nowhere of ours to go back to.
  if (location.key === 'default') {
    return (
      <Link to="/clients" className={className}>
        ← Clients
      </Link>
    );
  }

  return (
    <button type="button" onClick={() => navigate(-1)} className={className}>
      ← {state?.from ?? 'Back'}
    </button>
  );
}
